/**
 * RewardNotifier — checks for pending reward notifications on mount/balance refresh.
 * Shows one RewardDialog at a time, acknowledges after all are dismissed.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAccountStore } from '@/store/accountStore.ts';
import { getReferralInfo, acknowledgeRewards } from '@/api/rewardApi.ts';
import type { PendingReward } from '@/api/rewardApi.ts';
import { RewardDialog } from './RewardDialog.tsx';

export function RewardNotifier() {
  const accountUser = useAccountStore(s => s.accountUser);
  const [queue, setQueue] = useState<PendingReward[]>([]);
  const checkedRef = useRef(false);

  // Check for pending rewards when user is authenticated
  useEffect(() => {
    if (!accountUser || checkedRef.current) return;
    checkedRef.current = true;

    getReferralInfo()
      .then(info => {
        if (info.pending_rewards.length > 0) {
          setQueue(info.pending_rewards);
        }
      })
      .catch(() => { /* silent — reward check is non-critical */ });
  }, [accountUser]);

  const handleDismiss = useCallback(async () => {
    setQueue(prev => {
      const next = prev.slice(1);
      // When all rewards have been shown, acknowledge them on the server
      if (next.length === 0) {
        acknowledgeRewards().catch(() => {});
      }
      return next;
    });
  }, []);

  if (queue.length === 0) return null;

  return <RewardDialog reward={queue[0]} onDismiss={handleDismiss} />;
}
